import { ApplicationElement, html } from '../application_element/element.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import PageScrollManager from '../../lib/page_scroll_manager.js';
import InvalidFormHandler from '../../lib/invalid_form_handler.js';

import { modalController } from '@ionic/core';
import { defineCustomElement as defineIonModal } from '@ionic/core/components/ion-modal.js';
import { defineCustomElement as defineIonHeader } from '@ionic/core/components/ion-header.js';
import { defineCustomElement as defineIonToolbar } from '@ionic/core/components/ion-toolbar.js';
import { defineCustomElement as defineIonTitle } from '@ionic/core/components/ion-title.js';
import { defineCustomElement as defineIonButtons } from '@ionic/core/components/ion-buttons.js';
import { defineCustomElement as defineIonButton } from '@ionic/core/components/ion-button.js';
import { initialize } from '@ionic/core/components/index.js';

import translations from './translations.js';
import style from './style.lit.css';

// Ionic components setup
initialize();
defineIonModal();
defineIonHeader();
defineIonToolbar();
defineIonTitle();
defineIonButtons();
defineIonButton();

/**
 *
 - It uses the [ion-modal component](https://ionicframework.com/docs/api/modal) from Ionic.
 - It self-initializes once is rendered or appended to the DOM.
 - It will set automatically primary and secondary actions in the modal header if type="submit" and/or data-dismiss="modal" buttons are present in the modal content.
 - Two components with the same namespace cannot be opened at the same time. E.g. if the component with the same namespace is appended to the body, the first one will be deleted from the DOM.
 - It freezes the scroll position before opening and restores it after closing (for disabling iOS background scroll)
 - Passing a secondaryActionText will add a secondary action button in the modal header with the text provided. Clicking on it will close the modal by default. You can change this, as well as primaryAction by setting properties: handleMainAction and handleClose before opening modal.

 *
 * @prop content - will be set automatically using innerHTML
 * @prop primaryActionText - text for the primary action button - if there is a type="submit" button in the modal, its text will be used instead
 * @prop secondaryActionText - text for the secondary action button - if there is a data-dismiss="modal" button in the modal, its text will be used instead
 * @prop manual - if true, modal will not be opened automatically
 * @prop closeExisting - if true will close all modals in the same namespace
 * @prop namespace - if set, will replace content of any existing modal with the same namespace with the content of this one
 *
 * @event modal-content-updated - Dispatched when modal content is updated
 * @event ajax:success - Dispatched when ajax request is successful
 * @event ionModalWillPresent - Dispatched when modal is about to present
 * @event ionModalDidPresent - Dispatched when modal has presented
 * @event ionModalWillDismiss - Dispatched when modal is about to dismiss
 * @event ionModalDidDismiss - Dispatched when modal has dismissed
 *
 */

export default class Modal extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    content: { type: String },
    primaryActionText: { type: String },
    secondaryActionText: { type: String },
    manual: { type: Boolean },
    closeExisting: { type: Boolean },
    namespace: { type: String, reflect: true },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  modalContentRef = createRef();

  constructor() {
    super();

    /** @internal */
    this.pageScrollManager = new PageScrollManager(); // For disabling iOS background scroll
    this.primaryActionText = this.t('finish');
    this.secondaryActionText = '';
  }

  connectedCallback() {
    super.connectedCallback();

    this.preSetup();

    if (this.resolveExistingModal()) return;

    // Don't setup modal if closeExisting is ON and no existing modal found
    if (this.closeExisting) {
      console.warn(
        'ef-modal - closeExisting is ON but no existing modal found. Self destructing.'
      );
      this.remove();
      return;
    }

    this.setupModal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.pageScrollManager.restore();
    document.body.classList.remove('modal-open');
    this.modal?.remove();
  }

  createRenderRoot() {
    this.mainContainer = document.createElement('div');
    return this.mainContainer;
  }

  /** @internal */
  get modalContent() {
    return this.modalContentRef.value;
  }

  /** @internal */
  get existingModal() {
    const modal =
      this.namespace &&
      document.querySelector(`ef-modal[namespace=${this.namespace}]`);
    return modal !== this ? modal : null;
  }

  inject() {
    document.body.appendChild(this);
  }

  preSetup() {
    this.isForm = !!this.querySelector('form');
    this.primaryActionText =
      this.querySelector('[type="submit"]')?.textContent ||
      this.primaryActionText;

    this.secondaryActionText = this.isForm
      ? this.querySelector('[data-dismiss="modal"]')?.textContent
      : this.secondaryActionText;

    this.title = this.querySelector('.modal-title')?.textContent || '';
  }

  resolveExistingModal() {
    if (!this.existingModal) return;

    if (!this.closeExisting) {
      this.existingModal.content = this.innerHTML;
    } else {
      this.existingModal.dismiss();
    }

    this.existingModal.primaryActionText = this.primaryActionText;
    this.existingModal.secondaryActionText = this.secondaryActionText;
    this.existingModal.title = this.title;
    this.existingModal.isForm = this.isForm;
    this.existingModal.handleModalContentUpdated();
    this.remove();
    return true;
  }

  dispatchModalContentUpdated() {
    this.dispatchEvent(
      new Event('modal-content-updated', { bubbles: true, composed: true })
    );
  }

  handleModalContentUpdated() {
    this.modalContent && new InvalidFormHandler(this.modalContent).start();
    this.dispatchModalContentUpdated();
  }

  setupModalEvents() {
    this.modal.addEventListener('ionModalWillPresent', this);
    this.modal.addEventListener('ionModalDidPresent', this);
    this.modal.addEventListener('ionModalDidDismiss', this);
    this.modal.addEventListener('ajax:success', this);
  }

  handleEvent(event) {
    const { type } = event;
    type === 'ionModalWillPresent' && this.onWillPresent(event);
    type === 'ionModalDidPresent' && this.onDidPresent(event);
    type === 'ionModalDidDismiss' && this.onDidDismiss(event);
    type === 'ajax:success' && this.onFormSuccess(event);
  }

  onWillPresent() {
    this.pageScrollManager.set();
  }

  onDidPresent() {
    document.body.classList.add('modal-open');
    this.handleModalContentUpdated();
  }

  onDidDismiss() {
    this.remove();
  }

  onFormSuccess(e) {
    const xhr = e.detail[2];
    const isHTML = xhr.getResponseHeader('Content-Type')?.includes('html');

    // Close modal when response is empty
    if (!xhr.response || isHTML) this.dismiss();
  }

  handleMainAction() {
    const submitButton = this.modalContent.querySelector('[type="submit"]');
    submitButton ? submitButton.click() : this.dismiss();
  }

  handleClose() {
    this.dismiss();
  }

  prepareModalContent() {
    this.content = this.innerHTML;
  }

  async setupModal() {
    this.prepareModalContent();

    this.modal ||= await modalController.create({
      component: this.mainContainer,
    });

    this.setupModalEvents();

    // Even when modal hasn't been presented yet, ion prevents scrolling by adding 'backdrop-no-scroll' class to body
    if (this.manual) document.body.classList.remove('backdrop-no-scroll');
    else this.present();
  }

  handleAnyClick(e) {
    if (e.target.closest('[data-dismiss="modal"]')) this.dismiss();
  }

  present() {
    this.modal.present();
  }

  dismiss() {
    this.modal.dismiss();
  }

  renderPrimaryAction() {
    return html`
      <ion-button @click=${this.handleMainAction}>
        ${this.primaryActionText}
      </ion-button>
    `;
  }

  renderSecondaryAction() {
    return html`
      <ion-button @click=${this.handleClose}>
        ${this.secondaryActionText}
      </ion-button>
    `;
  }

  render() {
    return html`
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start">
            ${this.secondaryActionText ? this.renderSecondaryAction() : ''}
          </ion-buttons>
          <ion-title>${this.title}</ion-title>
          <ion-buttons slot="end">
            ${this.primaryActionText ? this.renderPrimaryAction() : ''}
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <div
        class="modal-content"
        ${ref(this.modalContentRef)}
        @click=${this.handleAnyClick}>
        ${unsafeHTML(this.content)}
      </div>
    `;
  }
}

Modal.registerTagName('ef-modal');

customElements.get('ef-modal') || customElements.define('ef-modal', Modal);
